/**
 * Phase 17.6: every instant the CMS shows is formatted in the signed-in person's time zone (Account Settings),
 * or the browser's when they haven't chosen one. Player-local times (schedules, restrictions, a screen's charts)
 * keep the player's zone and say so — they don't come through here.
 */
import { uiLocale } from '../i18n';
import { useAuthStore } from '../stores/auth.store';

export const browserTimeZone = (): string => Intl.DateTimeFormat().resolvedOptions().timeZone;

/** Reactive inside computeds and templates (reads the session). */
export function userTimeZone(): string {
  try {
    return useAuthStore().user?.timeZone ?? browserTimeZone();
  } catch {
    return browserTimeZone(); // before Pinia is installed
  }
}

export function formatInstant(value: string | number | Date, opts: Intl.DateTimeFormatOptions): string {
  const date = value instanceof Date ? value : new Date(value);
  try {
    return new Intl.DateTimeFormat(uiLocale(), { ...opts, timeZone: userTimeZone() }).format(date);
  } catch {
    return new Intl.DateTimeFormat(uiLocale(), opts).format(date);
  }
}

/** Every IANA zone the browser knows, for pickers (falls back to a short list on old browsers). */
export function timeZones(): string[] {
  const all = (Intl as { supportedValuesOf?: (k: string) => string[] }).supportedValuesOf?.('timeZone');
  return all?.length ? all : ['UTC', 'Asia/Dubai', 'Asia/Karachi', 'Asia/Riyadh', 'Europe/London', 'America/New_York'];
}
